import { Link } from 'react-router-dom';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  image: string;
  tech: string[];
  deployTime: string;
}

const templates: Template[] = [
  {
    id: 'nextjs-starter',
    name: 'Next.js Starter',
    description: 'Modern React framework with server-side rendering, static generation, and API routes.',
    category: 'Framework',
    image: '/templates/nextjs.svg',
    tech: ['React', 'TypeScript', 'Tailwind'],
    deployTime: '~45s',
  },
  {
    id: 'vite-react',
    name: 'Vite + React',
    description: 'Lightning-fast development with Vite and React. Perfect for SPAs and modern web apps.',
    category: 'Framework',
    image: '/templates/vite.svg',
    tech: ['React', 'Vite', 'TypeScript'],
    deployTime: '~30s',
  },
  {
    id: 'node-express',
    name: 'Node.js Express API',
    description: 'RESTful API starter with Express, middleware setup, and environment configuration.',
    category: 'Backend',
    image: '/templates/nodejs.svg',
    tech: ['Node.js', 'Express', 'REST'],
    deployTime: '~25s',
  },
  {
    id: 'python-fastapi',
    name: 'Python FastAPI',
    description: 'High-performance Python API with automatic OpenAPI documentation and async support.',
    category: 'Backend',
    image: '/templates/python.svg',
    tech: ['Python', 'FastAPI', 'Pydantic'],
    deployTime: '~35s',
  },
  {
    id: 'static-html',
    name: 'Static HTML/CSS',
    description: 'Simple static site template with modern CSS and responsive design.',
    category: 'Static',
    image: '/templates/html.svg',
    tech: ['HTML', 'CSS', 'JavaScript'],
    deployTime: '~15s',
  },
  {
    id: 'astro-blog',
    name: 'Astro Blog',
    description: 'Content-focused blog template with Astro. Markdown support and optimized performance.',
    category: 'Content',
    image: '/templates/astro.svg',
    tech: ['Astro', 'Markdown', 'MDX'],
    deployTime: '~40s',
  },
];

const categories = ['All', 'Framework', 'Backend', 'Static', 'Content'];

export default function TemplatesPage() {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Deploy in{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              Seconds
            </span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Start with a pre-configured template. Every template is optimized for production and includes best practices.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                cat === 'All'
                  ? 'bg-orange-500 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className="group bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1"
            >
              {/* Template Preview */}
              <div className="h-40 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                <div className="w-16 h-16 rounded-xl bg-zinc-700/50 flex items-center justify-center">
                  <span className="material-icons text-3xl text-zinc-400 group-hover:text-orange-400 transition">
                    code
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-orange-400 bg-orange-500/10 px-2 py-1 rounded">
                    {template.category}
                  </span>
                  <span className="text-xs text-zinc-500 flex items-center gap-1">
                    <span className="material-icons text-sm">schedule</span>
                    {template.deployTime}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{template.description}</p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {template.tech.map((t) => (
                    <span key={t} className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                      {t}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Link
                    to="/register"
                    className="flex-1 py-2 px-4 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-medium rounded-lg text-center hover:shadow-glow transition"
                  >
                    Deploy
                  </Link>
                  <button className="py-2 px-4 bg-zinc-800 text-zinc-400 text-sm rounded-lg hover:text-white hover:bg-zinc-700 transition">
                    Preview
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Template CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block p-8 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-2xl">
            <h2 className="text-2xl font-bold text-white mb-3">Need a custom template?</h2>
            <p className="text-zinc-400 mb-6 max-w-md">
              Deploy any Git repository or Docker container. WiserWebCloud auto-detects your stack.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-zinc-900 font-semibold rounded-lg hover:bg-zinc-100 transition"
            >
              <span className="material-icons">chat</span>
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
